import { useEffect, useState, useRef, useCallback } from 'react'
import { FieldValues, Path } from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon
} from '@clientbase/clientbase-library'
import { Box, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import moment from 'moment'

import { currency } from 'utils'
import { priceConverter } from 'utils/FormatMoney'
import {
  FREQUENCY_ENUM_TO_NAME,
  FrequencyEnum,
  presetToPair
} from 'utils/frequency'
import useDebounce from 'utils/useDebounce'

import { BonusStatus } from '../AdvancedOptions'
import { RecurrenceCycle } from '../Composition/Composition.interface'
import { CyclesAccordionProps } from './CyclesAccordion.interface'
import CyclesTable from './CyclesTable'

const CyclesAccordion = <T extends FieldValues>({
  isUnique,
  watch,
  isEditingCharge = false,
  isEditingRecurrence = false,
  editData,
  onCyclesChange
}: CyclesAccordionProps<T>) => {
  const [recurrenceCycles, setRecurrenceCycles] = useState<RecurrenceCycle[]>(
    []
  )
  const [isLoading, setIsLoading] = useState(false)

  // Espelha a prévia para o pai (o botão de preview fica no cabeçalho da
  // composição). Ref para não entrar nas dependências do efeito.
  const onCyclesChangeRef = useRef(onCyclesChange)
  onCyclesChangeRef.current = onCyclesChange
  useEffect(() => {
    onCyclesChangeRef.current?.(recurrenceCycles)
  }, [recurrenceCycles])

  // Referência para armazenar o último conjunto de itens formatados
  const lastItemsRef = useRef<any[]>([])

  // Função para formatar os itens para a API
  const formatItemsForAPI = useCallback((items: any[], watchFn: any) => {
    return items
      .map((_: any, index: number) => {
        const price = watchFn(`items.${index}.price` as Path<T>) || 0
        const quantity = watchFn(`items.${index}.quantity` as Path<T>) || 0
        const total = watchFn(`items.${index}.total` as Path<T>) || 0
        const description =
          watchFn(`items.${index}.description` as Path<T>) || ''
        const product = watchFn(`items.${index}.product` as Path<T>)
        const startCycle = Number(
          watchFn(`items.${index}.start_cycle` as Path<T>) ?? 1
        )
        const totalCycles = Number(
          watchFn(`items.${index}.total_cycles` as Path<T>) ?? 0
        )
        // Cadência do item: sem ela o preview mostraria o item em todo ciclo enquanto o
        // motor o coloca de N em N.
        const cycleInterval = Math.max(
          Number(watchFn(`items.${index}.cycle_interval` as Path<T>) ?? 1),
          1
        )
        const bonusPolicy =
          watchFn(`items.${index}.bonus_policy` as Path<T>) || 'no_bonus'
        const bonusAmount = watchFn(`items.${index}.bonus_amount` as Path<T>)
        const bonusStartCycle = Number(
          watchFn(`items.${index}.bonus_start_cycle` as Path<T>) ?? 1
        )
        const bonusTotalCycles = Number(
          watchFn(`items.${index}.bonus_total_cycles` as Path<T>) ?? 0
        )

        if (total === 0) return null
        if (price === 0) return null
        if (quantity === 0) return null
        if (!description && !product) return null

        // Só enviar a janela quando o item de fato a configurou. Item sem janela
        // (start 1 / total 0 / interval 1) fica de fora para preservar o comportamento
        // legado de product_total_cycles no backend (Recurrence.preview_cycles).
        // A cadência entra na condição: um item que só tem passo (herdado do plano) e
        // nenhuma janela precisa ser enviado, senão o preview o mostra em todo ciclo.
        const hasCycleWindow =
          startCycle > 1 || totalCycles > 0 || cycleInterval > 1

        // Bonificação reduz o amount_billed do ciclo — mandar para o preview refletir o
        // valor líquido. Percentual guarda número puro; R$ guarda centavos (priceConverter).
        const hasBonus = bonusPolicy !== 'no_bonus' && Number(bonusAmount) > 0

        return {
          amount_billed: total,
          quantity: quantity,
          amount_unit: quantity ? total / quantity : 0,
          ...(hasCycleWindow
            ? {
                start_cycle: startCycle,
                total_cycles: totalCycles,
                cycle_interval: cycleInterval
              }
            : {}),
          ...(hasBonus
            ? {
                bonus_policy: bonusPolicy,
                bonus_amount:
                  bonusPolicy === 'percentage'
                    ? Number(bonusAmount) || 0
                    : priceConverter(bonusAmount),
                // Janela da bonificação: o preview zera a bonificação fora dela, igual ao motor.
                bonus_start_cycle: Math.max(bonusStartCycle || 1, 1),
                bonus_total_cycles: Math.max(bonusTotalCycles || 0, 0)
              }
            : {}),
          ...(description
            ? { description: description }
            : product
            ? { product_uuid: product.value }
            : {})
        }
      })
      .filter(Boolean)
  }, [])

  // Função para obter a data de vencimento formatada
  const getFormattedDueDate = useCallback(
    (watchFn: any) => {
      const hasDueDate = watchFn('dueDate' as Path<T>) !== undefined
      const hasDueDay = watchFn('dueDay' as Path<T>) !== undefined

      if (isEditingRecurrence && hasDueDay) {
        // Usa a data original da recorrência apenas como base de mês/ano,
        // mas aplica o dia de vencimento atual (que pode ter sido editado pelo usuário).
        const day = Number(watchFn('dueDay' as Path<T>)) || 1
        const base =
          editData && (editData as any).firstDueDate
            ? moment((editData as any).firstDueDate)
            : moment()

        return base.date(day).format('YYYY-MM-DD')
      } else if (hasDueDate) {
        return moment(watchFn('dueDate' as Path<T>)).format('YYYY-MM-DD')
      } else if (hasDueDay) {
        return moment()
          .date(watchFn('dueDay' as Path<T>) || 1)
          .format('YYYY-MM-DD')
      } else {
        return moment().format('YYYY-MM-DD')
      }
    },
    [isEditingRecurrence, editData]
  )

  // Referência para armazenar o ID do timer
  const timerRef = useRef<number | null>(null)

  // Referência para armazenar a última vez que a API foi chamada
  const lastCallTimeRef = useRef<number>(0)

  // Referência para armazenar se a segunda chamada já foi feita
  const secondCallMadeRef = useRef<boolean>(false)

  // Assinatura do último payload enviado. A "segunda chamada" existe para pegar
  // valores que mudaram durante a digitação; quando nada mudou, ela repete o
  // mesmo POST e a requisição idêntica concorrente volta 409.
  const lastPayloadRef = useRef<string>('')

  // Função para fazer a chamada à API e atualizar o estado dos ciclos
  const fetchCyclesPreview = useCallback(
    async (
      formattedItems: any[],
      totalCycles: number,
      dueDate: string,
      frequency: number,
      isSecondCall = false
    ) => {
      // Se não houver itens formatados, limpa os ciclos e retorna
      if (formattedItems.length === 0) {
        setRecurrenceCycles([])
        return
      }

      // Atualiza o timestamp da última chamada
      lastCallTimeRef.current = Date.now()

      // Se for a segunda chamada, marca como feita
      if (isSecondCall) {
        secondCallMadeRef.current = true
      } else {
        // Se for a primeira chamada, reseta o flag da segunda chamada
        secondCallMadeRef.current = false
      }

      // Ativa o ícone de carregamento para ambas as chamadas
      setIsLoading(true)

      // Captura os dados de bonificação do formulário
      const hasBonus = watch('hasBonus' as Path<T>) || false
      const bonusSettings = (watch('bonusSettings' as Path<T>) as any) || {}

      // Captura o totalCyclesCustom para usar quando totalCycles for 13 (Outros)
      const totalCyclesCustom = watch('totalCyclesCustom' as Path<T>) || ''

      // Frequência personalizada (Personalizada = 7) envia o par (type, number)
      // para o motor unificado do backend.
      const frequencyType = watch('frequency_type' as Path<T>)
      const frequencyNumber = watch('frequency_number' as Path<T>)

      // Calcula o valor real de total_cycles
      const actualTotalCycles =
        totalCycles === 13 ? Number(totalCyclesCustom) || 1 : totalCycles

      // Constrói o bonus_attributes seguindo o mesmo padrão do BodyModalNewChargeOrRecurrence
      const bonusAttributes = {
        policy: !hasBonus
          ? BonusStatus.no_bonus
          : bonusSettings?.type === 'value'
          ? BonusStatus.fixed
          : BonusStatus.percentage,
        total_cycles:
          bonusSettings?.cyclesCount === 13
            ? Number(bonusSettings?.cyclesCountCustom)
            : bonusSettings?.cyclesCount,
        amount:
          bonusSettings?.type === 'value'
            ? bonusSettings?.amount
              ? Number(
                  currency(bonusSettings.amount)
                    .replace(/\./g, '')
                    .replace(',', '.')
                )
              : 0
            : Number(bonusSettings?.amount || 0),
        start_cycle: bonusSettings?.startCycle || 1
      }

      const payload = {
        billing_items: formattedItems,
        total_cycles: actualTotalCycles,
        first_due_date: dueDate,
        frequency,
        // O par canônico vai sempre junto; para presets o backend o ignora.
        ...(() => {
          const [unit, count] = presetToPair(
            FREQUENCY_ENUM_TO_NAME[frequency] ?? 'monthly',
            frequencyType,
            Number(frequencyNumber) || undefined
          )
          return { frequency_type: unit, frequency_number: count }
        })(),
        bonus_attributes: bonusAttributes,
        ...(isEditingRecurrence &&
          editData?.uuid && {
            recurrence_uuid: editData.uuid
          })
      }

      // A segunda chamada só tem razão de existir se algum valor mudou desde a
      // primeira. Repetir o mesmo payload gera a requisição idêntica que a API
      // recusa com 409, e a simulação exibida seria a mesma.
      const signature = JSON.stringify(payload)
      if (isSecondCall && signature === lastPayloadRef.current) {
        setIsLoading(false)
        return
      }
      lastPayloadRef.current = signature

      try {
        const response = await DataService({
          type: 'POST',
          url: 'v3/recurrences/preview_cycles',
          data: payload
        })

        if (response.status === 200 && response.data && response.data?.cycles) {
          setRecurrenceCycles(response.data.cycles)
        }
      } catch (error) {
        setRecurrenceCycles([])
      } finally {
        // Se for a primeira chamada, não desativa o loading ainda
        // O loading será desativado apenas após a segunda chamada ou após o timeout
        if (isSecondCall) {
          setIsLoading(false)
        }
      }
    },
    [isEditingRecurrence, editData]
  )

  // SC-10813: 10ms não agrupa digitação — cada tecla virava um POST, e a
  // rajada de requisições idênticas era rejeitada com 409 (2.110 erros em 10
  // dias, 149 merchants). 400ms espera o usuário parar de digitar antes de
  // simular os ciclos.
  const getCyclesPreview = useDebounce(fetchCyclesPreview, 400)

  // Função para verificar se é hora de fazer a segunda chamada
  const checkAndMakeSecondCall = useCallback(() => {
    const now = Date.now()
    const timeSinceLastCall = now - lastCallTimeRef.current

    // Se passou pelo menos 1 segundo desde a última chamada e a segunda chamada ainda não foi feita
    if (
      lastCallTimeRef.current > 0 &&
      timeSinceLastCall >= 1000 &&
      !secondCallMadeRef.current
    ) {
      // Captura os valores mais atualizados no momento da segunda chamada
      const items = watch('items' as Path<T>) || []
      const totalCycles = watch('totalCycles' as Path<T>)
      const totalCyclesCustom = watch('totalCyclesCustom' as Path<T>) || ''
      const frequency = (watch('frequency' as Path<T>) as number) ?? 1
      const frequencyType = watch('frequency_type' as Path<T>)
      const frequencyNumber = watch('frequency_number' as Path<T>)
      const isCustomIncomplete =
        frequency === FrequencyEnum.Personalizada &&
        (!frequencyType || !frequencyNumber)

      // Calcula o valor real de total_cycles
      const actualTotalCycles =
        totalCycles === 13 ? Number(totalCyclesCustom) || 1 : totalCycles || 0

      // Formata a data de vencimento mais atualizada
      const dueDate = getFormattedDueDate(watch)

      // Formata os itens para a API com os dados mais atualizados
      const formattedItems = formatItemsForAPI(items, watch)

      const isInvalidDate = isNaN(new Date(dueDate).getTime())

      if (formattedItems.length === 0 || isInvalidDate || isCustomIncomplete) {
        setIsLoading(false)
        return
      }

      // Faz a segunda chamada com os dados mais atualizados
      fetchCyclesPreview(
        formattedItems,
        actualTotalCycles,
        dueDate,
        frequency,
        true
      )
    } else if (
      lastCallTimeRef.current > 0 &&
      timeSinceLastCall >= 1500 &&
      !secondCallMadeRef.current
    ) {
      // Se passou 1.5 segundos e a segunda chamada ainda não foi feita,
      // desativa o loading para garantir que não fique preso
      setIsLoading(false)
    }
  }, [watch, fetchCyclesPreview, formatItemsForAPI, getFormattedDueDate])

  // Configura um timer que verifica a cada 100ms se é hora de fazer a segunda chamada
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    timerRef.current = window.setInterval(() => {
      checkAndMakeSecondCall()
    }, 100) as unknown as number

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      // Garante que o loading seja desativado quando o componente for desmontado
      setIsLoading(false)
    }
  }, [checkAndMakeSecondCall])

  // Observa mudanças nos itens e faz a primeira chamada para atualizar os ciclos
  useEffect(() => {
    // Se for uma cobrança única ou estiver editando uma cobrança, não faz a primeira chamada
    if (isUnique || isEditingCharge) return

    // Captura os valores atuais para garantir consistência
    const items = watch('items' as Path<T>) || []
    const totalCycles = watch('totalCycles' as Path<T>)
    const totalCyclesCustom = watch('totalCyclesCustom' as Path<T>) || ''
    const frequency = (watch('frequency' as Path<T>) as number) ?? 1
    const frequencyType = watch('frequency_type' as Path<T>)
    const frequencyNumber = watch('frequency_number' as Path<T>)

    // Frequência personalizada (7) só gera o preview quando o intervalo estiver
    // totalmente configurado (unidade + quantidade), assim como as frequências
    // pré-determinadas já vêm completas ao serem selecionadas.
    const isCustomIncomplete =
      frequency === FrequencyEnum.Personalizada &&
      (!frequencyType || !frequencyNumber)

    // Calcula o valor real de total_cycles
    const actualTotalCycles =
      totalCycles === 13 ? Number(totalCyclesCustom) || 1 : totalCycles || 0

    // Formata a data de vencimento
    const dueDate = getFormattedDueDate(watch)

    // Formata os itens para a API
    const formattedItems = formatItemsForAPI(items, watch)

    // Armazena os itens formatados para comparação futura
    lastItemsRef.current = formattedItems

    const isInvalidDate = isNaN(new Date(dueDate).getTime())

    if (formattedItems.length === 0 || isInvalidDate || isCustomIncomplete) {
      setIsLoading(false)
      return
    }

    // Primeira chamada para obter os ciclos
    getCyclesPreview(formattedItems, actualTotalCycles, dueDate, frequency)
  }, [
    isUnique,
    watch,
    getCyclesPreview,
    formatItemsForAPI,
    getFormattedDueDate,
    // Observa mudanças nos itens e seus campos
    watch('items' as Path<T>),
    watch('totalCycles' as Path<T>),
    watch('totalCyclesCustom' as Path<T>),
    watch('frequency' as Path<T>),
    watch('frequency_type' as Path<T>),
    watch('frequency_number' as Path<T>),
    watch('dueDate' as Path<T>),
    watch('dueDay' as Path<T>),
    // Observa mudanças nos dados de bonificação
    watch('hasBonus' as Path<T>),
    watch('bonusSettings.type' as Path<T>),
    watch('bonusSettings.cyclesCount' as Path<T>),
    watch('bonusSettings.cyclesCountCustom' as Path<T>),
    watch('bonusSettings.amount' as Path<T>),
    watch('bonusSettings.startCycle' as Path<T>),
    // Adiciona um observador para cada item individual
    ...Object.keys(watch('items' as Path<T>) || {}).flatMap((index) => [
      watch(`items.${index}.price` as Path<T>),
      watch(`items.${index}.quantity` as Path<T>),
      watch(`items.${index}.description` as Path<T>),
      watch(`items.${index}.product` as Path<T>),
      watch(`items.${index}.total` as Path<T>),
      watch(`items.${index}.start_cycle` as Path<T>),
      watch(`items.${index}.total_cycles` as Path<T>),
      // Bonificação do item e a janela dela (SC-11169): sem observar, mudar só a
      // bonificação deixava a prévia com o valor antigo.
      watch(`items.${index}.bonus_policy` as Path<T>),
      watch(`items.${index}.bonus_amount` as Path<T>),
      watch(`items.${index}.bonus_start_cycle` as Path<T>),
      watch(`items.${index}.bonus_total_cycles` as Path<T>)
    ])
  ])

  // Verifica se há pelo menos um item com description ou product.value
  const hasValidItems = (watch('items' as Path<T>) || []).some(
    (item: any) =>
      (item.description && item.description.trim() !== '') ||
      (item.product && item.product.value)
  )

  // Verifica se há pelo menos um produto selecionado
  const hasProductSelected = (watch('items' as Path<T>) || []).some(
    (item: any) => item && item.product && item.product.value
  )

  // Renderiza apenas a mensagem se houver produtos selecionados mas não mostrar o accordion
  if (
    hasProductSelected &&
    (isUnique ||
      isEditingCharge ||
      recurrenceCycles.length === 0 ||
      !hasValidItems)
  ) {
    return (
      <Box mb={2} mt={2}>
        <Typography variant="smBold" sx={{ fontSize: '14px' }}>
          *As configurações abaixo são aplicadas de acordo com o primeiro plano
          escolhido
        </Typography>
      </Box>
    )
  }

  // Não mostra nada se não houver produtos selecionados e não mostrar o accordion
  if (
    isUnique ||
    isEditingCharge ||
    recurrenceCycles.length === 0 ||
    !hasValidItems
  ) {
    return null
  }

  return (
    <>
      <Box mt={2}>
        <Accordion
          sx={{
            border: '1px solid #E8EBE8',
            borderLeft: '3px solid',
            borderLeftColor: 'primary.main',
            borderRadius: '8px !important',
            backgroundColor: '#FAFBFA',
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': {
              margin: 0,
              backgroundColor: '#fff'
            }
          }}
        >
          <AccordionSummary
            sx={{
              '.MuiAccordionSummary-content': {
                margin: '20px 0px'
              }
            }}
            aria-controls="cycles-content"
            id="cycles"
          >
            <Box display="flex" alignItems="center">
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginRight: '8px'
                }}
              >
                Ciclos
              </Typography>
              {isLoading && (
                <Icon
                  icon="cached"
                  sx={{
                    fontSize: '16px',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)'
                      },
                      '100%': {
                        transform: 'rotate(360deg)'
                      }
                    }
                  }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              maxHeight: '200px',
              overflow: 'auto',
              pr: 1
            }}
          >
            <CyclesTable cycles={recurrenceCycles} />
          </AccordionDetails>
        </Accordion>
      </Box>

      {hasProductSelected && (
        <Box mt={2} mb={2}>
          <Typography variant="smBold" sx={{ fontSize: '14px' }}>
            *As configurações abaixo são aplicadas de acordo com o primeiro
            plano escolhido
          </Typography>
        </Box>
      )}
    </>
  )
}

export default CyclesAccordion
